import type { BreadcrumbItem } from '@shared/ui';
import { isNilUuid } from '@shared/utils';
import { matchPath } from 'react-router-dom';

export type BreadcrumbMemberContext = {
  memberId?: string;
  memberLabel?: string;
};

export type BreadcrumbLabelContext = {
  artifactLabel?: string;
  summaryLabel?: string;
};

export type NonAdminBreadcrumbContext = {
  labels?: BreadcrumbLabelContext;
  memberContext?: BreadcrumbMemberContext | null;
  pathname: string;
  viewerMode?: 'coach' | 'member';
};

export type NonAdminBreadcrumbModel = {
  items: BreadcrumbItem[];
};

type RouteBuilder = (
  ctx: NonAdminBreadcrumbContext,
  params: Record<string, string | undefined>
) => BreadcrumbItem[];

type RouteDefinition = {
  build: RouteBuilder;
  path: string;
};

const MEMBERS_ROOT: BreadcrumbItem = { label: 'Members', to: '/members' };
const REFLECTIONS_ROOT: BreadcrumbItem = { label: 'Reflections', to: '/artifacts' };
const valueOrFallback = (value: string | undefined, fallback: string): string => {
  if (!value || value.trim().length === 0) {
    return fallback;
  }
  return value;
};

const decodeSegment = (value: string | undefined, fallback: string): string => {
  if (!value || value.trim().length === 0) {
    return fallback;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const memberCrumbFromContext = (
  memberContext: BreadcrumbMemberContext | null | undefined,
  memberIdFromRoute: string | undefined
): BreadcrumbItem | null => {
  const memberId = valueOrFallback(memberIdFromRoute, memberContext?.memberId ?? '');
  if (isNilUuid(memberId)) {
    return null;
  }
  const memberLabel = valueOrFallback(
    memberContext?.memberLabel,
    decodeSegment(memberId, 'Member')
  );
  if (memberLabel === 'Member' && memberId.trim().length === 0) {
    return null;
  }
  return memberId.trim().length > 0
    ? { label: memberLabel, to: `/members/${encodeURIComponent(memberId)}` }
    : { label: memberLabel };
};

const isMemberViewer = (ctx: NonAdminBreadcrumbContext): boolean => ctx.viewerMode === 'member';

const withCurrent = (items: BreadcrumbItem[]): BreadcrumbItem[] =>
  items.map((item, index) => (index === items.length - 1 ? { label: item.label } : item));

const providerLabel = (provider: string | undefined): string => {
  const raw = decodeSegment(provider, 'Provider');
  return raw
    .split('-')
    .filter((segment) => segment.length > 0)
    .map((segment) => `${segment[0].toUpperCase()}${segment.slice(1)}`)
    .join(' ');
};

const routeDefinitions: RouteDefinition[] = [
  { path: '/members', build: () => [{ label: 'Members' }] },
  {
    path: '/members/:personId',
    build: (ctx, params) => {
      const memberCrumb = memberCrumbFromContext(ctx.memberContext, params.personId);
      return withCurrent([
        MEMBERS_ROOT,
        memberCrumb ?? { label: decodeSegment(params.personId, 'Member') },
      ]);
    },
  },
  {
    path: '/members/:personId/sessions/:sessionId/transcript',
    build: (ctx, params) => {
      const memberCrumb = memberCrumbFromContext(ctx.memberContext, params.personId);
      return withCurrent([
        MEMBERS_ROOT,
        memberCrumb ?? { label: decodeSegment(params.personId, 'Member') },
        { label: 'Transcript' },
      ]);
    },
  },
  {
    path: '/artifacts',
    build: (ctx) => [{ label: isMemberViewer(ctx) ? 'Reflections' : 'All Artifacts' }],
  },
  {
    path: '/artifacts/:artifactId',
    build: (ctx, params) => {
      if (isMemberViewer(ctx)) {
        const artifactLabel = valueOrFallback(
          ctx.labels?.artifactLabel,
          `Artifact ${decodeSegment(params.artifactId, '')}`.trim()
        );
        return withCurrent([REFLECTIONS_ROOT, { label: artifactLabel }]);
      }

      const memberCrumb = memberCrumbFromContext(ctx.memberContext, undefined);
      const artifactLabel = valueOrFallback(
        ctx.labels?.artifactLabel,
        `Artifact ${decodeSegment(params.artifactId, '')}`.trim()
      );
      return withCurrent(
        memberCrumb
          ? [MEMBERS_ROOT, memberCrumb, { label: artifactLabel }]
          : [MEMBERS_ROOT, { label: artifactLabel }]
      );
    },
  },
  {
    path: '/drive1on1/summary',
    build: (ctx) => {
      const memberCrumb = memberCrumbFromContext(ctx.memberContext, undefined);
      const summaryLabel = valueOrFallback(ctx.labels?.summaryLabel, 'Session Summary');
      return withCurrent(
        memberCrumb
          ? [MEMBERS_ROOT, memberCrumb, { label: summaryLabel }]
          : [MEMBERS_ROOT, { label: summaryLabel }]
      );
    },
  },
  { path: '/chat', build: () => [{ label: 'Chat' }] },
  {
    path: '/chat/sidebar',
    build: () => withCurrent([{ label: 'Chat', to: '/chat' }, { label: 'Sidebar Chat' }]),
  },
  { path: '/agents', build: () => [{ label: 'Agents' }] },
  {
    path: '/settings/integrations',
    build: () => withCurrent([{ label: 'Settings' }, { label: 'Integrations' }]),
  },
  {
    path: '/settings/integrations/:provider',
    build: (_, params) =>
      withCurrent([
        { label: 'Settings' },
        { label: 'Integrations', to: '/settings/integrations' },
        { label: providerLabel(params.provider) },
      ]),
  },
];

export const buildNonAdminBreadcrumbs = (
  context: NonAdminBreadcrumbContext
): NonAdminBreadcrumbModel | null => {
  for (const definition of routeDefinitions) {
    const match = matchPath({ path: definition.path, end: true }, context.pathname);
    if (!match) {
      continue;
    }

    const items = definition.build(context, match.params);
    return {
      items,
    };
  }

  return null;
};
